import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { Button } from '@/components/ui/button';
import { SavedFiltersSidebar } from '@/components/SavedFiltersSidebar';
import { dashboardAPI, organizationAPI, verticalAPI } from '@/lib/api';
import { formatCurrency, getMonthName, getMonthOptions, getYearOptions, getPreviousMonth, getPreviousMonthYear } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import SalaryPayoutTable from '@/components/dashboard/SalaryPayoutTable';
import {
  Building2,
  Users,
  Layers,
  FileSpreadsheet,
  Clock,
  CheckCircle,
  Banknote,
  TrendingDown,
  Loader2,
  Filter,
  Gift,
  Bookmark,
} from 'lucide-react';

export default function DashboardPage() {
  const { canViewSalary } = useAuth();
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters - Default to previous month
  const [filterMonth, setFilterMonth] = useState(getPreviousMonth().toString());
  const [filterYear, setFilterYear] = useState(getPreviousMonthYear().toString());
  const [filterOrgs, setFilterOrgs] = useState([]);
  const [filterVerticals, setFilterVerticals] = useState([]);
  
  // Saved Filters Sidebar
  const [savedFiltersOpen, setSavedFiltersOpen] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (!loading || stats) {
      loadStats();
    }
  }, [filterMonth, filterYear, filterOrgs, filterVerticals]);

  const loadInitialData = async () => {
    try {
      const [orgRes, vertRes] = await Promise.all([
        organizationAPI.getAll(),
        verticalAPI.getAll(),
      ]);
      setOrganizations(orgRes.data);
      setVerticals(vertRes.data);
      await loadStats();
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const params = {
        month: parseInt(filterMonth),
        year: parseInt(filterYear),
      };
      if (filterOrgs.length > 0) params.organization_ids = filterOrgs.join(',');
      if (filterVerticals.length > 0) params.vertical_ids = filterVerticals.join(',');
      
      const response = await dashboardAPI.getStats(params);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  // Get current filters for saving
  const getCurrentFilters = () => ({
    month: filterMonth,
    year: filterYear,
    organizations: filterOrgs,
    verticals: filterVerticals,
  });

  // Apply saved filter
  const handleApplyFilter = (filterConfig) => {
    if (filterConfig.month) setFilterMonth(filterConfig.month);
    if (filterConfig.year) setFilterYear(filterConfig.year);
    if (filterConfig.organizations) setFilterOrgs(filterConfig.organizations);
    if (filterConfig.verticals) setFilterVerticals(filterConfig.verticals);
  };

  // Filter labels for display in sidebar
  const filterLabels = {
    month: 'Month',
    year: 'Year',
    organizations: 'Organizations',
    verticals: 'Verticals',
  };

  const monthOptions = getMonthOptions();
  const yearOptions = getYearOptions();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 spinner text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  const overviewCards = [
    {
      title: 'Organizations',
      value: stats?.organizations || 0,
      icon: Building2,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-950',
    },
    {
      title: 'Employees',
      value: stats?.employees || 0,
      icon: Users,
      color: 'text-emerald-600 dark:text-emerald-400',
      bgColor: 'bg-emerald-50 dark:bg-emerald-950',
    },
    {
      title: 'Verticals',
      value: stats?.verticals || 0,
      icon: Layers,
      color: 'text-violet-600 dark:text-violet-400',
      bgColor: 'bg-violet-50 dark:bg-violet-950',
    },
    {
      title: 'Records This Month',
      value: stats?.current_month?.total_records || 0,
      icon: FileSpreadsheet,
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-50 dark:bg-amber-950',
    },
  ];

  const monthlyStats = [
    {
      title: 'Pending Finalization',
      value: stats?.current_month?.pending_finalize || 0,
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50 dark:bg-orange-950',
      status: 'status-pending',
    },
    {
      title: 'Awaiting Payment',
      value: stats?.current_month?.pending_payment || 0,
      icon: Banknote,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50 dark:bg-amber-950',
      status: 'status-finalized',
    },
    {
      title: 'Paid',
      value: stats?.current_month?.paid || 0,
      icon: CheckCircle,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50 dark:bg-emerald-950',
      status: 'status-paid',
    },
  ];

  return (
    <DashboardLayout>
      <div data-testid="dashboard-page" className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Overview of your salary reporting system
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 p-4 bg-muted/30 rounded-lg">
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger className="w-[140px]" data-testid="dashboard-filter-month">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((m) => (
                <SelectItem key={m.value} value={m.value.toString()}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-[100px]" data-testid="dashboard-filter-year">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={y.toString()}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="w-[220px]">
            <MultiSelect
              options={organizations.map(org => ({ value: org.id, label: org.name }))}
              selected={filterOrgs}
              onChange={setFilterOrgs}
              placeholder="All Organizations"
              data-testid="dashboard-filter-org"
            />
          </div>

          <div className="w-[180px]">
            <MultiSelect
              options={verticals.map(v => ({ value: v.id, label: v.name }))}
              selected={filterVerticals}
              onChange={setFilterVerticals}
              placeholder="All Verticals"
              data-testid="dashboard-filter-vertical"
            />
          </div>

          <Button
            variant="outline"
            size="default"
            onClick={() => setSavedFiltersOpen(true)}
            data-testid="open-saved-filters-btn"
          >
            <Bookmark className="h-4 w-4 mr-2" />
            Saved Filters
          </Button>
        </div>

        {/* Saved Filters Sidebar */}
        <SavedFiltersSidebar
          isOpen={savedFiltersOpen}
          onClose={() => setSavedFiltersOpen(false)}
          page="dashboard"
          currentFilters={getCurrentFilters()}
          onApplyFilter={handleApplyFilter}
          filterLabels={filterLabels}
        />

        {/* Overview Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {overviewCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Card
                key={card.title}
                className="card-hover"
                data-testid={`stat-card-${card.title.toLowerCase().replace(' ', '-')}`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        {card.title}
                      </p>
                      <p className="text-3xl font-bold mt-2 font-mono">{card.value}</p>
                    </div>
                    <div className={`h-12 w-12 rounded-xl ${card.bgColor} flex items-center justify-center`}>
                      <Icon className={`h-6 w-6 ${card.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Current Month Section */}
        <div>
          <h2 className="text-xl font-semibold mb-4">
            {getMonthName(parseInt(filterMonth))} {filterYear} Overview
            {(filterOrgs.length > 0 || filterVerticals.length > 0) && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                (Filtered)
              </span>
            )}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {monthlyStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card
                  key={stat.title}
                  className="card-hover"
                  data-testid={`monthly-stat-${stat.title.toLowerCase().replace(' ', '-')}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className={`h-14 w-14 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                        <Icon className={`h-7 w-7 ${stat.color}`} />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{stat.title}</p>
                        <p className="text-2xl font-bold font-mono">{stat.value}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Financial Summary - Only show for users with salary view permission */}
          {canViewSalary() && (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
              <Card className="card-hover">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Total Salary Payable
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold font-mono text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(stats?.current_month?.total_salary || 0)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    After absent deductions
                  </p>
                </CardContent>
              </Card>

              <Card className="card-hover">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Total Absent Deductions
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold font-mono text-red-600 dark:text-red-400">
                      {formatCurrency(stats?.current_month?.total_deductions || 0)}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Due to days absent
                    </p>
                  </div>
                  <TrendingDown className="h-8 w-8 text-red-400" />
                </CardContent>
              </Card>

              <Card className="card-hover">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Retention Held
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold font-mono text-amber-600 dark:text-amber-400">
                    {formatCurrency(stats?.current_month?.total_retention || 0)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Monthly retention
                  </p>
                </CardContent>
              </Card>

              <Card className="card-hover">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Total Rewards
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold font-mono text-violet-600 dark:text-violet-400">
                      {formatCurrency(stats?.current_month?.total_rewards || 0)}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Bonus rewards
                    </p>
                  </div>
                  <Gift className="h-8 w-8 text-violet-400" />
                </CardContent>
              </Card>
            </div>
          )}

          {/* LOP Days Manager Summary - Show only absent-related stats */}
          {!canViewSalary() && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="card-hover">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Total LOP Days
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold font-mono text-orange-600 dark:text-orange-400">
                    {stats?.current_month?.total_lop_days || 0}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    This month
                  </p>
                </CardContent>
              </Card>

              <Card className="card-hover">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Records Processed
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold font-mono text-blue-600 dark:text-blue-400">
                    {stats?.current_month?.total_records || 0}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Total records
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Monthly Salary Payout by Organization & Vertical */}
        {canViewSalary() && <SalaryPayoutTable />}

        {/* Quick Tips */}
        <Card className="bg-muted/50">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-3">Quick Tips</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Use the <strong>Salary Reports</strong> page to enter LOP Days and manage payments</li>
              <li>• <strong>Bulk Entry</strong> allows you to enter salary data for all employees at once</li>
              <li>• Export reports to CSV or Excel for accounting purposes</li>
              <li>• Use the filters above to view data for specific organizations or verticals</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
